/**
 * Controles de ciclo de vida da narrativa do laudo (E9.F2 → E23.F23). // ux-neutrality:allow doc-comment
 *
 * E23.F23 enxugou este componente: o editor de texto campo-a-campo saiu (a // ux-neutrality:allow doc-comment
 * edição agora acontece ao lado do PDF — conversacional + editar trecho). Aqui // ux-neutrality:allow doc-comment
 * ficam só os controles de CICLO por contestada: gerar, aprovar e reabrir. // ux-neutrality:allow doc-comment
 *
 * Renderizado por tab da contestada (`key={caseAnalysisId}` no pai). Apenas o // ux-neutrality:allow doc-comment
 * titular do escritório (role=owner) gera/aprova/reabre. // ux-neutrality:allow doc-comment
 */
import { useCallback, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { useCurrentRole } from '@/lib/auth/useCurrentRole';
import {
  approveNarrative,
  generateNarrative,
  getNarrative,
  LaudoError,
  unapproveNarrative,
  type LaudoErrorReason,
  type NarrativeRecordData,
} from '@/lib/laudo';

// EM.F3.T01: mensagem honesta por motivo. Timeout do Agente não é falta de
// chave — e o texto pode ter sido gerado mesmo assim (recarregar).
function narrativeGenerateMessage(reason: LaudoErrorReason): string {
  if (reason === 'assistant_timeout') return ptBR.laudoCaso.narrative.generateErrorTimeout;
  if (reason === 'no_api_key' || reason === 'owner_required')
    return ptBR.laudoCaso.narrative.generateErrorNoKey;
  return ptBR.laudoCaso.narrative.generateError;
}

// E137.F2.C04 (GUS-160): a aprovação é do texto que está na tela. Recusada porque o texto mudou,
// a tela traz a versão atual para revisar; recusada porque está sendo gerado, pede para aguardar.
function narrativeApproveMessage(reason: LaudoErrorReason): string {
  if (reason === 'narrativa_desatualizada')
    return ptBR.laudoCaso.narrative.approval.approveErrorStale;
  if (reason === 'narrativa_em_geracao')
    return ptBR.laudoCaso.narrative.approval.approveErrorGenerating;
  return ptBR.laudoCaso.narrative.approval.approveError;
}

interface LaudoNarrativeEditorProps {
  caseId: string;
  caseAnalysisId: string;
  contestadaLetra: string;
  narrative: NarrativeRecordData | null;
  onNarrativeChanged: (analysisId: string, narrative: NarrativeRecordData | null) => void;
  // E36.F5/ADR-0050: a narrativa está sendo regenerada em background após um
  // ajuste de LR na mesa. Trava gerar/aprovar e mostra o indicador.
  regenerating?: boolean;
}

export function LaudoNarrativeEditor({
  caseId,
  caseAnalysisId,
  contestadaLetra,
  narrative,
  onNarrativeChanged,
  regenerating = false,
}: LaudoNarrativeEditorProps): JSX.Element {
  const client = useApiClient();
  const { role } = useCurrentRole();
  const isOwner = role === 'owner';
  const isApproved = narrative?.approved_at != null;

  const [generating, setGenerating] = useState(false);
  const [approving, setApproving] = useState(false);
  const [confirmRegenerateOpen, setConfirmRegenerateOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const doGenerate = useCallback(async () => {
    setGenerating(true);
    setErrorMessage(null);
    try {
      const record = await generateNarrative(client, caseId, caseAnalysisId);
      onNarrativeChanged(caseAnalysisId, record);
    } catch (err) {
      // EM.F3.T01: mensagem honesta por motivo; console.error preserva o erro
      // original pra debug no DevTools (sem expor jargão ao usuário).
      console.error('generateNarrative failed', err);
      const reason: LaudoErrorReason = err instanceof LaudoError ? err.reason : 'unknown';
      setErrorMessage(narrativeGenerateMessage(reason));
    } finally {
      setGenerating(false);
    }
  }, [caseAnalysisId, caseId, client, onNarrativeChanged]);

  const handleGenerate = useCallback(() => {
    if (narrative !== null) {
      setConfirmRegenerateOpen(true);
      return;
    }
    void doGenerate();
  }, [narrative, doGenerate]);

  const handleConfirmRegenerate = useCallback(async () => {
    setConfirmRegenerateOpen(false);
    await doGenerate();
  }, [doGenerate]);

  const handleApprove = useCallback(async () => {
    if (!narrative) return;
    setApproving(true);
    setErrorMessage(null);
    try {
      const updated = await approveNarrative(
        client,
        caseId,
        caseAnalysisId,
        narrative.texto_sha256,
      );
      onNarrativeChanged(caseAnalysisId, updated);
    } catch (err) {
      const reason: LaudoErrorReason = err instanceof LaudoError ? err.reason : 'unknown';
      setErrorMessage(narrativeApproveMessage(reason));
      if (reason === 'narrativa_desatualizada') {
        const atual = await getNarrative(client, caseId, caseAnalysisId).catch(() => undefined);
        if (atual !== undefined) onNarrativeChanged(caseAnalysisId, atual);
      }
    } finally {
      setApproving(false);
    }
  }, [caseAnalysisId, caseId, client, narrative, onNarrativeChanged]);

  const handleReopen = useCallback(async () => {
    if (!narrative) return;
    setApproving(true);
    setErrorMessage(null);
    try {
      const updated = await unapproveNarrative(client, caseId, caseAnalysisId);
      onNarrativeChanged(caseAnalysisId, updated);
    } catch {
      setErrorMessage(ptBR.laudoCaso.narrative.approval.reopenError);
    } finally {
      setApproving(false);
    }
  }, [caseAnalysisId, caseId, client, narrative, onNarrativeChanged]);

  return (
    <section
      className="laudo-narrative"
      role="tabpanel"
      id={`laudo-tab-panel-${caseAnalysisId}`}
      aria-labelledby={`laudo-tab-${caseAnalysisId}`}
      data-testid="laudo-narrative-editor"
      data-contestada-letra={contestadaLetra}
    >
      <NarrativeHeader
        isOwner={isOwner}
        hasNarrative={narrative !== null}
        generating={generating}
        approving={approving}
        isApproved={isApproved}
        regenerating={regenerating}
        onGenerate={handleGenerate}
        onApprove={handleApprove}
        onReopen={handleReopen}
      />
      {regenerating && (
        <p
          className="laudo-narrative__notice"
          role="status"
          data-testid="laudo-narrative-regenerating"
        >
          {ptBR.laudoCaso.narrative.regenerating}
        </p>
      )}
      {!isOwner && (
        <p className="laudo-narrative__notice" role="note">
          {ptBR.laudoCaso.narrative.readOnlyForMember}
        </p>
      )}
      {!narrative && isOwner && (
        <p className="laudo-narrative__notice" role="note" data-testid="laudo-narrative-empty-hint">
          {ptBR.laudoCaso.narrative.emptyHint}
        </p>
      )}
      {errorMessage && (
        <p role="alert" className="laudo-narrative__error">
          {errorMessage}
        </p>
      )}
      {isApproved && (
        <p
          className="laudo-narrative__approved-helper"
          role="status"
          data-testid="laudo-narrative-approved-helper"
        >
          {ptBR.laudoCaso.narrative.approval.approvedHelper}
        </p>
      )}
      <ConfirmModal
        open={confirmRegenerateOpen}
        onOpenChange={setConfirmRegenerateOpen}
        title={ptBR.laudoCaso.regenerateConfirmTitle}
        description={ptBR.laudoCaso.regenerateConfirmBody}
        confirmLabel={ptBR.laudoCaso.regenerateConfirmAction}
        cancelLabel={ptBR.laudoCaso.regenerateCancel}
        variant="destructive"
        onConfirm={() => void handleConfirmRegenerate()}
        busy={generating}
      />
    </section>
  );
}

interface NarrativeHeaderProps {
  isOwner: boolean;
  hasNarrative: boolean;
  generating: boolean;
  approving: boolean;
  isApproved: boolean;
  regenerating: boolean;
  onGenerate: () => void;
  onApprove: () => void;
  onReopen: () => void;
}

// E23.F23: sem título redundante ("Texto do laudo a)") — a tab da contestada já
// dá o contexto. Aqui ficam só os controles de ciclo, alinhados à direita.
function NarrativeHeader({
  isOwner,
  hasNarrative,
  generating,
  approving,
  isApproved,
  regenerating,
  onGenerate,
  onApprove,
  onReopen,
}: NarrativeHeaderProps): JSX.Element {
  return (
    <header className="laudo-narrative__header laudo-narrative__header--actions-only">
      {isOwner && (
        <NarrativeHeaderActions
          hasNarrative={hasNarrative}
          generating={generating}
          approving={approving}
          isApproved={isApproved}
          regenerating={regenerating}
          onGenerate={onGenerate}
          onApprove={onApprove}
          onReopen={onReopen}
        />
      )}
    </header>
  );
}

interface NarrativeHeaderActionsProps {
  hasNarrative: boolean;
  generating: boolean;
  approving: boolean;
  isApproved: boolean;
  regenerating: boolean;
  onGenerate: () => void;
  onApprove: () => void;
  onReopen: () => void;
}

function NarrativeHeaderActions({
  hasNarrative,
  generating,
  approving,
  isApproved,
  regenerating,
  onGenerate,
  onApprove,
  onReopen,
}: NarrativeHeaderActionsProps): JSX.Element {
  return (
    <div className="laudo-narrative__actions">
      {hasNarrative && !isApproved && (
        <ApproveButton
          approving={approving}
          disabled={approving || regenerating || generating}
          onApprove={onApprove}
        />
      )}
      {hasNarrative && isApproved && (
        <ReopenButton
          approving={approving}
          disabled={approving || regenerating}
          onReopen={onReopen}
        />
      )}
      {!isApproved && (
        <GenerateButton
          generating={generating}
          hasNarrative={hasNarrative}
          disabled={generating || regenerating}
          onGenerate={onGenerate}
        />
      )}
    </div>
  );
}

function ApproveButton({
  approving,
  disabled,
  onApprove,
}: {
  approving: boolean;
  disabled: boolean;
  onApprove: () => void;
}): JSX.Element {
  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      onClick={onApprove}
      disabled={disabled}
      className="border-success text-success-700 hover:bg-success-50"
      data-testid="laudo-narrative-approve-btn"
    >
      {approving
        ? ptBR.laudoCaso.narrative.approval.approving
        : ptBR.laudoCaso.narrative.approval.approveCta}
    </Button>
  );
}

function ReopenButton({
  approving,
  disabled,
  onReopen,
}: {
  approving: boolean;
  disabled: boolean;
  onReopen: () => void;
}): JSX.Element {
  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      onClick={onReopen}
      disabled={disabled}
      data-testid="laudo-narrative-reopen-btn"
    >
      {approving
        ? ptBR.laudoCaso.narrative.approval.reopening
        : ptBR.laudoCaso.narrative.approval.reopenCta}
    </Button>
  );
}

function GenerateButton({
  generating,
  hasNarrative,
  disabled,
  onGenerate,
}: {
  generating: boolean;
  hasNarrative: boolean;
  disabled: boolean;
  onGenerate: () => void;
}): JSX.Element {
  const label = generating
    ? ptBR.laudoCaso.narrative.generating
    : hasNarrative
      ? ptBR.laudoCaso.narrative.regenerateCta
      : ptBR.laudoCaso.narrative.generateCta;
  return (
    <Button
      type="button"
      size="sm"
      onClick={onGenerate}
      disabled={disabled}
      data-testid="laudo-narrative-generate-btn"
    >
      {label}
    </Button>
  );
}
